import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import PageSeo from "components/Seo/PageSeo";
import { ENROLLMENT_DASHBOARD_ENABLED } from "config/featureFlags";
import { fetchDashboardData } from "helpers/dashboard/fetchDashboardData";
import {
  clearDashboardToken,
  getDashboardToken,
} from "helpers/dashboard/dashboardStorage";
import OwnerDashboardLogin from "./OwnerDashboardLogin";
import BusinessHealth from "./BusinessHealth";
import ActionPlan from "./ActionPlan";
import DemandSupplyGaps from "./DemandSupplyGaps";
import EnrollmentInsights from "./EnrollmentInsights";
import MeetPageAnalytics from "./MeetPageAnalytics";
import DashboardDetails from "./DashboardDetails";
import DashboardLoading from "./DashboardLoading";
import DashboardShell from "./DashboardShell";
import DashboardAdvisor from "./DashboardAdvisor";
import { isValidDashboardView } from "./dashboardViews";
import "./ownerDashboard.css";

const VIEW_STORAGE_KEY = "ace_dashboard_view";

const getInitialView = () => {
  const stored = sessionStorage.getItem(VIEW_STORAGE_KEY);
  return isValidDashboardView(stored) ? stored : "overview";
};

const OwnerDashboard = () => {
  const [token, setToken] = useState(() => getDashboardToken());
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(token));
  const [error, setError] = useState("");
  const [activeView, setActiveView] = useState(getInitialView);
  const [advisorOpen, setAdvisorOpen] = useState(false);

  const handleViewChange = (viewId) => {
    setActiveView(viewId);
    sessionStorage.setItem(VIEW_STORAGE_KEY, viewId);
  };

  const loadDashboard = useCallback(async (authToken) => {
    setIsLoading(true);
    setError("");

    try {
      const payload = await fetchDashboardData(authToken);
      setData(payload);
      setToken(authToken);
    } catch (loadError) {
      if (loadError.status === 401) {
        clearDashboardToken();
        setToken(null);
        setData(null);
        setError("Incorrect password. Try again.");
      } else {
        setError(loadError.message || "Could not load dashboard data.");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    loadDashboard(token);
  }, [token, loadDashboard]);

  if (!ENROLLMENT_DASHBOARD_ENABLED) {
    return <Navigate to="/" replace />;
  }

  const handleSignOut = () => {
    clearDashboardToken();
    setToken(null);
    setData(null);
    setError("");
  };

  const generatedLabel = data?.generatedAt
    ? new Date(data.generatedAt).toLocaleString()
    : null;

  const renderViewContent = () => {
    if (!data) return null;

    switch (activeView) {
      case "enrollment":
        return (
          <>
            <ActionPlan
              recommendations={data.recommendations}
              capacity={data.capacity}
            />
            <DemandSupplyGaps gaps={data.demandSupplyGaps} />
            <DashboardDetails
              capacity={data.capacity}
              ga4={data.ga4}
              defaultOpen
            />
          </>
        );
      case "analytics":
        return (
          <EnrollmentInsights
            enrollment={data.enrollment}
            ga4={data.ga4}
            capacity={data.capacity}
          />
        );
      case "meet-analytics":
        return (
          <MeetPageAnalytics
            enrollment={data.enrollment}
            ga4={data.ga4}
          />
        );
      case "overview":
      default:
        return (
          <BusinessHealth
            health={data.health}
            dateRange={data.dateRange}
            ga4={data.ga4}
          />
        );
    }
  };

  return (
    <div className="owner-dashboard-page">
      <div className="owner-dashboard">
        <PageSeo
          title="Enrollment Dashboard"
          description="Private enrollment insights and recommendations for Ace Gymnastics owners."
          path="/owner/dashboard"
          noindex
        />

        {!token ? (
          <OwnerDashboardLogin errorMessage={error} onSuccess={loadDashboard} />
        ) : (
          <>
            <header className="owner-dashboard__topbar">
              <div className="owner-dashboard__topbar-brand">
                <p className="owner-dashboard__eyebrow">Ace Gymnastics</p>
                <h1>Owner dashboard</h1>
              </div>
              <div className="owner-dashboard__topbar-actions">
                {generatedLabel && (
                  <p className="owner-dashboard__timestamp">
                    Updated {generatedLabel}
                  </p>
                )}
                {data?.ai?.configured && (
                  <button
                    type="button"
                    className="owner-dashboard__advisor-topbar-btn"
                    onClick={() => setAdvisorOpen(true)}
                    aria-expanded={advisorOpen}
                  >
                    Business advisor
                  </button>
                )}
                <button
                  type="button"
                  className="owner-dashboard__refresh"
                  onClick={() => loadDashboard(token)}
                  disabled={isLoading}
                >
                  {isLoading ? "Refreshing…" : "Refresh data"}
                </button>
                <button
                  type="button"
                  className="owner-dashboard__signout"
                  onClick={handleSignOut}
                >
                  Sign out
                </button>
              </div>
            </header>

            {error && (
              <p
                className="owner-dashboard__banner owner-dashboard__banner--error"
                role="alert"
              >
                {error}
              </p>
            )}

            {data?.ga4?.error &&
              (activeView === "analytics" || activeView === "meet-analytics") && (
              <p
                className="owner-dashboard__banner owner-dashboard__banner--warn"
                role="status"
              >
                Website stats partially unavailable: {data.ga4.error}
              </p>
            )}

            {isLoading && !data ? (
              <DashboardLoading />
            ) : (
              data && (
                <>
                  <DashboardShell
                    activeView={activeView}
                    onViewChange={handleViewChange}
                    ga4Connected={data.ga4?.connected}
                  >
                    {renderViewContent()}
                  </DashboardShell>
                  <DashboardAdvisor
                    authToken={token}
                    aiConfigured={data.ai?.configured}
                    activeView={activeView}
                    isOpen={advisorOpen}
                    onOpenChange={setAdvisorOpen}
                  />
                </>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OwnerDashboard;
